<template>
  <div class="whats-new-container">
    <!-- Backdrop overlay -->
    <div class="whats-new-backdrop" @click="dismiss"></div>

    <!-- Pointer arrow -->
    <div class="whats-new-pointer" :style="pointerStyle">
      <div class="pointer-arrow"></div>
    </div>

    <!-- Main modal content -->
    <div class="whats-new-modal" :style="modalStyle">
      <div class="modal-header">
        <h3 class="title is-4 has-text-primary">
          <span class="icon">
            <i class="las la-star"></i>
          </span>
          What's New
        </h3>
        <button class="delete" @click="dismiss" aria-label="Close"></button>
      </div>

      <div class="modal-body">
        <div class="feature-highlight">
          <div class="feature-icon">
            <i class="las la-globe-americas has-text-primary"></i>
          </div>
          <div class="feature-content">
            <h4 class="subtitle is-5 has-text-weight-semibold">
              Multiple Service Regions
            </h4>
            <p class="has-text-grey-dark">
              You can now easily switch between different service regions! Click
              on your current region in the header to see all available areas
              and switch between them instantly.
            </p>
          </div>
        </div>
        <button
          class="button is-primary is-medium is-fullwidth"
          @click="dismiss"
        >
          Got it!
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'whats-new',
  data() {
    return {
      pointerStyle: {},
      modalStyle: {},
      observer: null,
      positioningRetryScheduled: false,
      positioningAttempts: 0,
      maxPositioningAttempts: 5,
    };
  },
  computed: {
    ...mapState({
      appVersion: state => state.header.appVersion,
    }),
  },
  mounted() {
    // Calculate initially and after a small delay to ensure DOM is fully rendered
    this.calculatePositioning();

    // Retry with increasing delays to ensure elements are loaded
    const retryIntervals = [300, 600, 1000, 2000];
    retryIntervals.forEach((delay, index) => {
      setTimeout(() => {
        if (this.positioningAttempts < this.maxPositioningAttempts) {
          console.log(`WhatsNew: Positioning attempt ${index + 1}`);
          this.calculatePositioning();
        }
      }, delay);
    });

    // Recalculate on resize
    window.addEventListener('resize', this.calculatePositioning);

    // MutationObserver to watch for DOM changes that might affect positioning
    this.setupDomObserver();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculatePositioning);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    dismiss() {
      try {
        // Always store current version in localStorage when dismissed
        // This way the alert won't show again for this version but will for future versions
        const currentVersion = this.appVersion || '1.5.4';
        console.log(`WhatsNew: Got app version: ${currentVersion}`);

        localStorage.setItem('whatsNewDismissed', currentVersion);
        console.log(`WhatsNew: Dismissed for version ${currentVersion}`);

        this.$modal.hide('WhatsNew');
      } catch (err) {
        console.error('WhatsNew: Error dismissing modal:', err);
        // Force hide with fallback method if modal plugin fails
        const modal = document.querySelector('.whats-new-container');
        if (modal && modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
      }
    },

    setupDomObserver() {
      // Create a MutationObserver to watch for DOM changes
      this.observer = new MutationObserver(() => {
        this.calculatePositioning();
      });

      try {
        // Start observing the document with configured parameters
        this.observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      } catch (err) {
        console.error('WhatsNew: Error setting up DOM observer:', err);
      }
    },

    calculatePositioning() {
      this.$nextTick(() => {
        try {
          // Find the header location display element
          const locationElement = document.querySelector(
            '.location-dropdown-target, .header-location-display'
          );
          const headerElement = document.querySelector('.cw-header');

          if (locationElement && headerElement) {
            const locationRect = locationElement.getBoundingClientRect();
            const headerRect = headerElement.getBoundingClientRect();

            // Check if rectangles are valid
            if (locationRect.width && headerRect.width) {
              // Position pointer to point to the location dropdown
              this.pointerStyle = {
                top: `${headerRect.bottom - 10}px`,
                left: `${locationRect.left + locationRect.width / 2 - 10}px`,
              };

              // Position modal directly below the pointer with no gap
              this.modalStyle = {
                top: `${headerRect.bottom + 5}px`,
                left: '50%',
                transform: 'translateX(-50%)',
              };

              // Successfully positioned
              return;
            }
          }

          // Fallback positioning if elements not found or invalid
          this.pointerStyle = {
            display: 'none', // Hide the pointer if we can't position it correctly
          };

          this.modalStyle = {
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
          };
        } catch (err) {
          console.error('WhatsNew: Error calculating position:', err);

          // Error fallback
          this.pointerStyle = { display: 'none' };
          this.modalStyle = {
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
          };
        }
      });

      // Call again with a slight delay to ensure everything is stable
      // Use a static variable to avoid infinite loops
      if (!this.positioningRetryScheduled) {
        this.positioningRetryScheduled = true;
        setTimeout(() => {
          this.positioningRetryScheduled = false;
          this.calculatePositioning();
        }, 100);
      }
    },
  },
};
</script>

<style scoped>
.whats-new-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  pointer-events: all;
}

.whats-new-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.whats-new-pointer {
  position: absolute;
  z-index: 10001;
}

.pointer-arrow {
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-bottom: 20px solid white;
  filter: drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.1));
  margin-bottom: -1px; /* Eliminate gap between pointer and modal */
  position: relative;
  z-index: 10002; /* Ensure pointer appears above modal top edge */
}

.whats-new-modal {
  position: absolute;
  z-index: 10000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  animation: slideInDown 0.3s ease-out;
  margin-top: 0; /* Remove any default margin */
  border-top-left-radius: 4px; /* Subtle corner radius at top */
  border-top-right-radius: 4px; /* Subtle corner radius at top */
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f5f5f5;
}

.modal-header .title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.feature-highlight {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.feature-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 50%;
  font-size: 1.5rem;
}

.feature-content {
  flex: 1;
}

.feature-content .subtitle {
  margin-bottom: 0.5rem;
}

.highlight-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.highlight-icon {
  flex-shrink: 0;
  font-size: 1.25rem;
}

.modal-footer {
  padding: 1rem 1.5rem 1.5rem;
  text-align: center;
}

.checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .whats-new-modal {
    width: 95%;
    max-width: none;
    margin: 0 auto;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .feature-highlight {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .highlight-box {
    flex-direction: column;
    text-align: center;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .whats-new-modal {
    background: #2d3748;
    color: white;
  }

  .modal-header {
    border-bottom-color: #4a5568;
  }

  .highlight-box {
    background: #4a5568;
    border-color: #718096;
  }

  .pointer-arrow {
    border-bottom-color: #2d3748;
  }

  .feature-icon {
    background: #4a5568;
  }
}
</style>
